import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../api/client';
import { categoriesApi, operationRequestsApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { CreateOperationRequestBody, OperationRequest, PaymentMethod } from '../api/types';
import { duplicateSuspectedPayload } from '../lib/duplicates';
import { newSourceKey } from '../lib/idempotency';
import { formatKopeks, rubToKopeks } from '../lib/money';
import { AmountInput, positiveAmountRule } from './AmountInput';

const DATE_FORMAT = 'DD.MM.YYYY';
const SALARY_CATEGORY_NAME = 'Зарплата';

/** The four money entries a seller can submit. Each becomes an OperationRequest
 * (awaiting admin approval) — never a direct ledger write. */
type EntryKind = 'CASH' | 'TERMINAL' | 'SALARY' | 'EXPENSE';
const KINDS: EntryKind[] = ['CASH', 'TERMINAL', 'SALARY', 'EXPENSE'];

/** Which entry kind a pending request came from, so the form can show only the
 * ones the seller is about to type again. SALARY and EXPENSE are both EXPENSE
 * operations, so расход rows are shown for either расход form. */
function matchesKind(req: OperationRequest, k: EntryKind): boolean {
  if (k === 'CASH' || k === 'TERMINAL') {
    return req.type === 'STORE_PAYMENT' && req.paymentMethod === k;
  }
  return req.type === 'EXPENSE';
}

interface EntryFormValues {
  amount: string;
  date: Dayjs;
  categoryId?: string;
  deductFromStoreBalance?: boolean;
  comment?: string;
}

interface SubmitInput {
  kind: EntryKind;
  values: EntryFormValues;
  /** Re-send after the look-alike warning: «да, это другая оплата». */
  confirmDuplicate?: boolean;
}

/**
 * Seller entry (SPEC §1 approval flow): a bottom drawer offering наличные /
 * терминал / зарплата / расход. Store is implicit (the seller's own). Every
 * submit posts an OperationRequest that an admin must approve before it touches
 * the balance — the success toast says «отправлено на утверждение».
 */
export function SellerEntryDrawer({
  open,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  /** The just-created request, so the home page can point at it. */
  onSubmitted?: (request: OperationRequest) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<EntryKind | null>(null);
  const [form] = Form.useForm<EntryFormValues>();

  // One key per submit ATTEMPT: retrying an unchanged entry (lost response,
  // token refresh mid-POST) returns the row the first try created instead of a
  // second money entry. Minted when an entry begins, whenever the values change
  // (an edited entry is a NEW attempt — reusing the key would return the stale
  // row and hide the edit), and after a successful submit.
  const sourceKeyRef = useRef<string>(newSourceKey());

  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(false),
    queryFn: () => categoriesApi.list(false),
    enabled: open,
  });
  const categories = categoriesQuery.data ?? [];
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

  // What is already waiting, shown BEFORE the seller types. A pending entry
  // moves no balance, so without this the seller has no evidence their earlier
  // entry landed — which is exactly what makes them type it again.
  const pendingQuery = useQuery({
    queryKey: queryKeys.operationRequests({ mine: true, status: 'PENDING' }),
    queryFn: () => operationRequestsApi.listMine('PENDING'),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (input: SubmitInput) => {
      const { kind: k, values } = input;
      const base = {
        date: values.date.format('YYYY-MM-DD'),
        amount: rubToKopeks(values.amount),
        ...(values.comment?.trim() ? { comment: values.comment.trim() } : {}),
        ...(input.confirmDuplicate ? { confirmDuplicate: true } : {}),
      };
      // «Вычесть из остатка магазина» applies to both расход kinds (SALARY and
      // EXPENSE are both EXPENSE operations against the seller's own store).
      const deduct = values.deductFromStoreBalance ? { deductFromStoreBalance: true } : {};
      let body: CreateOperationRequestBody;
      if (k === 'CASH' || k === 'TERMINAL') {
        body = { ...base, type: 'STORE_PAYMENT', paymentMethod: k as PaymentMethod };
      } else if (k === 'SALARY') {
        const salary = categories.find((c) => c.name === SALARY_CATEGORY_NAME);
        if (!salary) throw new Error(t('seller.salaryCategoryMissing'));
        body = { ...base, type: 'EXPENSE', categoryId: salary.id, ...deduct };
      } else {
        body = { ...base, type: 'EXPENSE', categoryId: values.categoryId, ...deduct };
      }
      return operationRequestsApi.create(body, sourceKeyRef.current);
    },
    onSuccess: (created, input) => {
      // Echo WHAT was sent, not just that something was — the seller needs
      // evidence, and a bare «отправлено» gives them none.
      message.success(
        t('seller.submittedDetailed', {
          kind: t(`seller.kind.${input.kind}`),
          amount: formatKopeks(created.amount),
          date: dayjs(created.date).format(DATE_FORMAT),
        }),
        5,
      );
      invalidateMoneyData(queryClient);
      form.resetFields();
      form.setFieldsValue({ date: dayjs() });
      setKind(null);
      sourceKeyRef.current = newSourceKey(); // next entry is a new attempt
      // Close, so the seller lands on their own list and SEES the row waiting.
      onSubmitted?.(created);
      onClose();
    },
    onError: (error, input) => {
      const dup = duplicateSuspectedPayload(error);
      if (dup) {
        modal.confirm({
          title: t('seller.duplicateTitle'),
          content: (
            <div>
              {dup.duplicates.map((d) => (
                <div key={d.id}>
                  {t('seller.duplicateBody', {
                    amount: formatKopeks(d.amount),
                    date: dayjs(d.date).format(DATE_FORMAT),
                    time: dayjs(d.createdAt).format('HH:mm'),
                    status: t(`seller.status.${d.status}`),
                  })}
                </div>
              ))}
              <div style={{ marginTop: 8 }}>{t('seller.duplicateQuestion')}</div>
            </div>
          ),
          okText: t('seller.duplicateOk'),
          cancelText: t('seller.duplicateCancel'),
          okButtonProps: { danger: true },
          // Same sourceKey on purpose: this is the SAME attempt. If the first
          // POST actually committed and only its response was lost, this returns
          // that row instead of creating a twin.
          onOk: () =>
            mutation.mutateAsync({ ...input, confirmDuplicate: true }).then(() => undefined),
        });
        return;
      }
      message.error(apiErrorMessage(error) ?? (error as Error).message ?? t('common.requestError'));
    },
  });

  const pending = pendingQuery.data ?? [];

  const mainView = (
    <div style={{ display: 'grid', gap: 8 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        {t('seller.entryHint')}
      </Typography.Paragraph>
      {KINDS.map((k) => (
        <Button
          key={k}
          size="large"
          style={{ justifyContent: 'flex-start', height: 48 }}
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({ date: dayjs() });
            sourceKeyRef.current = newSourceKey(); // a new entry begins
            setKind(k);
          }}
          block
        >
          {t(`seller.kind.${k}`)}
        </Button>
      ))}
    </div>
  );

  const entryView = (k: EntryKind): JSX.Element => {
    const samePending = pending.filter((req) => matchesKind(req, k));
    return (
      <Form<EntryFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ date: dayjs() }}
        // An edited entry is a NEW attempt: mint a fresh key so a retry can
        // never return the row a previous, different attempt created.
        onValuesChange={() => {
          sourceKeyRef.current = newSourceKey();
        }}
        onFinish={(values) => mutation.mutate({ kind: k, values })}
      >
        {samePending.length > 0 ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('seller.pendingTitle')}
            description={
              <div>
                {samePending.map((req) => (
                  <div key={req.id}>
                    {t('seller.alreadySentItem', {
                      amount: formatKopeks(req.amount),
                      date: dayjs(req.date).format(DATE_FORMAT),
                      time: dayjs(req.createdAt).format('HH:mm'),
                    })}
                  </div>
                ))}
                <div style={{ marginTop: 4 }}>{t('seller.pendingHint')}</div>
              </div>
            }
          />
        ) : null}
        {k === 'EXPENSE' ? (
          <Form.Item
            name="categoryId"
            label={t('common.category')}
            rules={[{ required: true, message: t('common.categoryRequired') }]}
          >
            <Select
              size="large"
              placeholder={t('quick.categoryPlaceholder')}
              options={categoryOptions}
              loading={categoriesQuery.isLoading}
            />
          </Form.Item>
        ) : null}
        <Form.Item name="amount" label={t('common.amount')} rules={[positiveAmountRule(t)]}>
          <AmountInput />
        </Form.Item>
        {k === 'EXPENSE' || k === 'SALARY' ? (
          <Form.Item
            name="deductFromStoreBalance"
            label={t('quick.deductFromStore')}
            tooltip={t('quick.deductFromStoreHint')}
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        ) : null}
        <Form.Item
          name="date"
          label={t('common.date')}
          rules={[{ required: true, message: t('common.dateRequired') }]}
        >
          <DatePicker
            size="large"
            style={{ width: '100%' }}
            format={DATE_FORMAT}
            allowClear={false}
            // Body-mounted (overrides the app-wide getPopupContainer): inside the
            // drawer's clipped container the panel can open past the viewport
            // top, hiding the month/year header on short mobile screens.
            getPopupContainer={() => document.body}
            inputReadOnly
          />
        </Form.Item>
        <Form.Item name="comment" label={t('common.comment')}>
          <Input
            size="large"
            placeholder={t('common.commentPlaceholder')}
            autoComplete="off"
            maxLength={1000}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" size="large" block loading={mutation.isPending}>
          {t('seller.submit')}
        </Button>
      </Form>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        setKind(null);
        onClose();
      }}
      placement="bottom"
      height="80%"
      title={
        kind === null ? (
          t('seller.entryTitle')
        ) : (
          <span>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => setKind(null)}
              style={{ marginInlineEnd: 8 }}
              aria-label={t('common.back')}
            />
            {t(`seller.kind.${kind}`)}
          </span>
        )
      }
      styles={{ body: { padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' } }}
    >
      {kind === null ? mainView : entryView(kind)}
    </Drawer>
  );
}
